import { X } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { RiskBadge } from "../../components/ui/RiskBadge.js";
import { ScoreBar } from "../../components/ui/ScoreBar.js";

export type RiskListKind = "high" | "medium" | "confirmedLeaving";

/**
 * Gemeinsames Prädikat für die Listen-Kategorien: bestätigte Abgänge sind eine
 * eigene Kategorie und zählen NICHT zusätzlich in high/medium hinein.
 */
export function matchesRiskListKind(
  e: { riskLevel: "high" | "medium" | "low" | null; isConfirmedLeaving?: boolean },
  kind: RiskListKind,
): boolean {
  return kind === "confirmedLeaving"
    ? Boolean(e.isConfirmedLeaving)
    : e.riskLevel === kind && !e.isConfirmedLeaving;
}

// ── Risk List Modal ────────────────────────────────────────────────────────

export interface RiskListEmployee {
  id: number;
  displayName: string;
  department: string | null;
  retentionScore: number | null;
  riskLevel: "high" | "medium" | "low" | null;
  topSignals: string[];
  terminationDate?: string | null;
  terminationReason?: string | null;
}

export function RiskListModal({
  kind,
  employees,
  onClose,
  onSelect,
}: {
  kind: RiskListKind;
  employees: RiskListEmployee[];
  onClose: () => void;
  onSelect: (id: number) => void;
}) {
  const { t } = useTranslation();

  // Close on Escape (gleiche Konvention wie EmployeeDetailDrawer)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const titleLabel =
    kind === "high"
      ? t("cockpit.riskList.high")
      : kind === "medium"
        ? t("cockpit.riskList.medium")
        : t("cockpit.riskList.leaving");
  const color =
    kind === "high"
      ? "var(--risk-high)"
      : kind === "medium"
        ? "var(--risk-medium)"
        : "var(--risk-high)";
  const sorted = [...employees].sort((a, b) => {
    if (kind === "confirmedLeaving") {
      // Nach Austrittsdatum aufsteigend (nächste zuerst)
      const ta = a.terminationDate
        ? new Date(a.terminationDate).getTime()
        : Number.POSITIVE_INFINITY;
      const tb = b.terminationDate
        ? new Date(b.terminationDate).getTime()
        : Number.POSITIVE_INFINITY;
      return ta - tb;
    }
    return (b.retentionScore ?? 0) - (a.retentionScore ?? 0);
  });

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.35)" }}
      onClick={onClose}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: natives <dialog> bringt UA-Styles
          und Top-Layer-Verhalten mit, die das card-Layout brechen würden; Overlay +
          role="dialog" + aria-modal + Escape-Handler decken die Semantik ab. */}
      <div
        role="dialog"
        aria-modal="true"
        className="card w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-5 py-4 flex items-center justify-between shrink-0"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: color }} />
            <h2 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
              {titleLabel} · {t("cockpit.riskList.count", { count: sorted.length })}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded transition-colors"
            style={{ color: "var(--text-muted)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--bg-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
            aria-label={t("cockpit.riskList.close")}
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {sorted.length === 0 ? (
            <p className="text-xs py-8 text-center" style={{ color: "var(--text-muted)" }}>
              {t("cockpit.riskList.empty")}
            </p>
          ) : (
            <div className="flex flex-col">
              {sorted.map((emp) => (
                <button
                  key={emp.id}
                  type="button"
                  onClick={() => onSelect(emp.id)}
                  className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded text-left transition-colors"
                  style={{ borderBottom: "1px solid var(--border)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--bg-hover)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="font-mono-data text-xs font-medium truncate"
                        style={{ color: "var(--text-primary)" }}
                      >
                        {emp.displayName}
                      </span>
                      {emp.riskLevel && <RiskBadge level={emp.riskLevel} />}
                    </div>
                    <p className="text-xs truncate" style={{ color: "var(--text-muted)" }}>
                      {kind === "confirmedLeaving"
                        ? `${emp.department ?? "—"}${emp.terminationReason ? ` · ${emp.terminationReason}` : ""}`
                        : `${emp.department ?? "—"}${emp.topSignals[0] ? ` · ${emp.topSignals[0]}` : ""}`}
                    </p>
                  </div>
                  {kind === "confirmedLeaving"
                    ? emp.terminationDate && (
                        <span
                          className="text-xs font-mono-data tabular-nums shrink-0"
                          style={{ color: "var(--risk-high)" }}
                        >
                          {new Date(emp.terminationDate).toLocaleDateString("de-DE", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit",
                          })}
                        </span>
                      )
                    : emp.retentionScore !== null &&
                      emp.riskLevel && (
                        <ScoreBar score={emp.retentionScore} riskLevel={emp.riskLevel} />
                      )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
